import React, { useEffect, useState } from 'react';
import { Container } from 'react-smooth-dnd';
import { cloneDeep, isEmpty } from 'lodash';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import { Avatar, Link } from '@superops/ui-design-system';
import { ApiProgressLoader, EventEmitter, Loader } from '@nile/components';
import { useReducerWithMiddleWare } from '@nile/graphql/src';
import boardReducer from './boardReducer';
import List from './List';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FormattedMessage } from 'react-intl';
import { RefreshButtonStyles } from '@nile/components/src/Components/SuperTable/TableStyles.js';

/**
 * This is a superboard
 * @component
 */

function SuperBoard(props) {
  const {
    getListItems,
    onLaneScroll,
    onCardDrop,
    components,
    displayLaneCount,
    lists,
    initial,
    reorderColumns,
    pinColumns,
    pinnedColumns = [],
    middleWares = [],
    sideEffects = [],
    restrictCardDragCheck,
    showLiveUpdate,
    enableLiveUpdate,
    onMessage,
    onLiveUpdateRefresh,
    liveUpdateCondition,
    wsPayload,
    isLiveUpdateInProgress,
    registerPageDelay
  } = props;

  const flags = useFlags();

  const broadcastChannelRef = React.useRef(null);
  if (enableLiveUpdate && !broadcastChannelRef?.current) {
    broadcastChannelRef.current = new BroadcastChannel('SuperShare');
  }

  const sendWSEvent = ({ event, payloadData }) => {
    if (!isEmpty(payloadData) && broadcastChannelRef.current) {
      const postMessageData = {
        context: 'pageUpdate',
        payload: {
          type: 'CLIENT_EVENT',
          event: event,
          epochMillis: new Date().getTime().toString(),
          ...payloadData
        }
      };

      broadcastChannelRef.current.postMessage(
        JSON.stringify({
          name: 'wsdata',
          data: postMessageData
        })
      );
    }
  };

  /**
   * We collapse lists when we have lists more than displayLaneCount length.
   * This method returns the lists which are displayed without collapsing
   * @function
   * @public
   */
  function getListsToRender() {
    const listsToRender = props.lists.slice(0, displayLaneCount);
    return listsToRender;
  }

  const initialItems = getListsToRender() || [];

  const [board, dispatch] = useReducerWithMiddleWare(
    boardReducer,
    initialItems,
    middleWares,
    sideEffects
  );

  const [refreshLists, setRefreshLists] = useState(false);

  /**
   * This method returns the lists which needs to be collapsed
   * @function
   * @public
   */
  const getOtherLists = () => {
    const listsToRender = props.lists.filter((el) => {
      return !board.some((li) => li?.id === el?.id);
    });
    return listsToRender;
  };
  const otherLists = getOtherLists();

  /**
   * This method removes the entire column from the open columns list
   * @param {object} li
   * @function
   * @public
   */
  const removeFromList = (li) => {
    dispatch({ type: 'removeFromList', list: li });
  };

  /**
   * This method pins/unpins selected column
   * @param {object} li
   * @function
   * @public
   */
  const togglePinnedColumn = (li) => {
    const meta = {
      boardIndex: board.findIndex((col) => col.id === li.id),
      pinnedIndex: pinnedColumns?.length,
      data: li
    };
    if (pinnedColumns?.includes(li.id)) {
      dispatch({ type: 'unpinColumn', meta: meta });
    } else {
      dispatch({ type: 'pinColumn', meta: meta });
    }
  };

  const actions = {
    removeFromList: removeFromList,
    togglePinnedColumn: togglePinnedColumn
  };

  /**
   * This gets triggered when the entire list is dragged and dropped
   * @param {object} meta
   * @function
   * @public
   */
  const onColumnDrop = (meta) => {
    if (meta.addedIndex !== null) {
      const clonedMeta = cloneDeep(meta);
      clonedMeta.addedIndex = meta.addedIndex + pinnedColumns?.length;
      clonedMeta.removedIndex = meta.removedIndex + pinnedColumns?.length;
      clonedMeta.payload.item = board[clonedMeta.removedIndex];
      dispatch({
        type: 'moveList',
        meta: clonedMeta
      });
      if (props.onListDrop) {
        const updatedBoard = cloneDeep(board);
        updatedBoard.splice(clonedMeta.removedIndex, 1);
        updatedBoard.splice(clonedMeta.addedIndex, 0, clonedMeta.payload.item);
        props.onListDrop(updatedBoard, board, dispatch);
      }
    }
  };

  /**
   * This method gets triggered when we drag any list
   * The data we pass in this method will be used when the list is dropped
   * @param {Number} idx
   * @function
   * @public
   */
  const getListPayload = (idx) => {
    return { lists: board, idx: idx, item: board[idx] };
  };

  const refreshBoard = () => {
    setRefreshLists(!refreshLists);
  };

  // When there is an event from WS this function will pass it to parent
  if (enableLiveUpdate) {
    broadcastChannelRef.current.onmessage = (event) => {
      if (onMessage && enableLiveUpdate) {
        if (event?.data) {
          const eventData = JSON.parse(event?.data);
          onMessage(eventData);
        }
      }
    };
  }

  const eventRef = React.useRef('REGISTER_PAGE');
  const isPageRegisteredRef = React.useRef(false);
  let registerEventTimeOutId = null;
  // Whenever "liveUpdateCondition" changes we'll call liveUpdateCondition
  useEffect(() => {
    if (
      enableLiveUpdate &&
      !isEmpty(wsPayload) &&
      eventRef.current === 'LIST_VIEW_UPDATE'
    ) {
      sendWSEvent({ event: 'LIST_VIEW_UPDATE', payloadData: wsPayload });
    }
  }, [liveUpdateCondition, wsPayload]);

  useEffect(() => {
    EventEmitter.subscribe('refreshBoard', refreshBoard);
    if (
      enableLiveUpdate &&
      !isEmpty(wsPayload) &&
      eventRef.current === 'REGISTER_PAGE'
    ) {
      if (registerEventTimeOutId) {
        clearTimeout(registerEventTimeOutId);
      }
      // UseEffect will be called on initialisation and connect to websocket
      if (registerPageDelay) {
        registerEventTimeOutId = setTimeout(() => {
          sendWSEvent({ event: 'REGISTER_PAGE', payloadData: wsPayload });
          eventRef.current = 'LIST_VIEW_UPDATE';
          isPageRegisteredRef.current = true;
        }, registerPageDelay);
      } else {
        sendWSEvent({ event: 'REGISTER_PAGE', payloadData: wsPayload });
        eventRef.current = 'LIST_VIEW_UPDATE';
        isPageRegisteredRef.current = true;
      }
    }
    return () => {
      EventEmitter.unsubscribe('refreshBoard');
      if (
        enableLiveUpdate &&
        !isEmpty(wsPayload) &&
        isPageRegisteredRef.current
      ) {
        // Sending UNREGISTER_PAGE and close broadcastChannel based on enableLiveUpdate flag
        sendWSEvent({ event: 'UNREGISTER_PAGE', payloadData: wsPayload });
        eventRef.current = 'REGISTER_PAGE';
        isPageRegisteredRef.current = false;
        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.close();
        }
      }
    };
  }, []);

  useEffect(() => {
    dispatch({ type: 'resetBoard', board: getListsToRender() });
  }, [lists]);

  const pinnedLists = board.filter((b) => pinnedColumns?.includes(b.id));

  const onRefresh = () => {
    if (onLiveUpdateRefresh) {
      onLiveUpdateRefresh();
    }
  };

  if (props?.loading) {
    return (
      <div className="full-height full-width">
        <Loader />
      </div>
    );
  }

  return (
    <SuperWrapper>
      {showLiveUpdate && (
        <RefreshButtonStyles
          className="refresh-notification fx actr g10"
          styles={props?.refreshButtonStyles}
        >
          {isLiveUpdateInProgress ? (
            <div className="flex g10 actr">
              <ApiProgressLoader isLoading />
              <FormattedMessage id="common.updating" />
            </div>
          ) : (
            <>
              <SVGIcon
                size="sm"
                color="var(--sops-white-to-white)"
                name="Lightning"
              />
              <FormattedMessage id="app.common.new.updates" />
              <div onClick={onRefresh} className="refresh pointer">
                <FormattedMessage id="app.common.refresh" />
              </div>
            </>
          )}
        </RefreshButtonStyles>
      )}
      <div className="super-list-holder full-width full-height">
        {pinColumns && (
          <PinnedColumnsContainer pinnedColumns={pinnedLists.length}>
            {pinnedLists.map((l) => {
              return (
                <List
                  key={`${l?.id}-${l?.title}`}
                  board={board}
                  refreshLists={refreshLists}
                  onLaneScroll={onLaneScroll}
                  list={l}
                  getListItems={getListItems}
                  dispatch={dispatch}
                  onCardDrop={onCardDrop}
                  components={components}
                  pinnedColumn
                  actions={actions}
                  refreshComplete={refreshBoard}
                  restrictCardDragCheck={restrictCardDragCheck}
                />
              );
            })}
          </PinnedColumnsContainer>
        )}
        <VisibleColumnsContainer
          className="kanban-columns-container"
          moveEnabled={reorderColumns}
          pinnedColumns={pinnedLists?.length}
        >
          {reorderColumns ? (
            <Container
              orientation="horizontal"
              onDrop={onColumnDrop}
              getChildPayload={(index) => getListPayload(index)}
              dragHandleSelector=".column-drag-handle"
              dropPlaceholder={{
                animationDuration: 150,
                showOnTop: true,
                className: 'cards-drop-preview'
              }}
            >
              {board
                .filter((b) => !pinnedColumns?.includes(b.id))
                .map((l) => {
                  return (
                    <List
                      key={`${l?.id}-${l?.title}`}
                      board={board}
                      refreshLists={refreshLists}
                      onLaneScroll={onLaneScroll}
                      list={l}
                      getListItems={getListItems}
                      dispatch={dispatch}
                      onCardDrop={onCardDrop}
                      components={components}
                      actions={actions}
                      refreshComplete={refreshBoard}
                      restrictCardDragCheck={restrictCardDragCheck}
                    />
                  );
                })}
              <OtherLists
                lists={otherLists}
                dispatch={dispatch}
                initial={initial}
              />
            </Container>
          ) : (
            <div className="smooth-dnd-container horizontal">
              {board
                .filter((b) => !pinnedColumns?.includes(b.id))
                .map((l) => {
                  return (
                    <List
                      key={l?.id}
                      board={board}
                      refreshLists={refreshLists}
                      onLaneScroll={onLaneScroll}
                      list={l}
                      getListItems={getListItems}
                      dispatch={dispatch}
                      onCardDrop={onCardDrop}
                      components={components}
                      actions={actions}
                      refreshComplete={refreshBoard}
                      restrictCardDragCheck={restrictCardDragCheck}
                    />
                  );
                })}
              <OtherLists
                lists={otherLists}
                dispatch={dispatch}
                initial={initial}
              />
            </div>
          )}
        </VisibleColumnsContainer>
      </div>
    </SuperWrapper>
  );
}

/**
 * This is the other list component which displays all collapsed lists. Clicking on a list will load this in to the board.
 * @public
 * @component
 */
function OtherLists(props) {
  const { lists, dispatch, initial } = props;
  const addList = (li) => {
    dispatch({ type: 'addList', list: li });
  };
  const openAllColumns = () => {
    dispatch({ type: 'addLists', lists: lists });
  };
  return (
    <OtherListsHolder className="super-other-lists">
      {lists.map((el) => {
        return (
          <OtherListHeader key={el.id} onClick={() => addList(el)}>
            <HeaderLabel className="fx actr">
              {initial && (
                <Avatar
                  name={el.title}
                  initials={el.title[0]}
                  size="extraSmall"
                />
              )}
              <span className="ellipsis ml-10">{el.title}</span>
              {/*count data not available for other cards list*/}
              {/* <div className="ml-10 count">{`${el.count || '0'}`}</div> */}
            </HeaderLabel>
            <SVGIcon name="ArrowRight" size="lg" />
          </OtherListHeader>
        );
      })}
      {lists.length > 3 && (
        <OtherListHeader onClick={openAllColumns}>
          <LoadMoreLabel className="fx actr jctr">
            Expand all Columns
          </LoadMoreLabel>
        </OtherListHeader>
      )}
    </OtherListsHolder>
  );
}

const OtherListsHolder = styled.div`
  // min-width: 300px;
  height: 100%;
  overflow: auto;
`;

const SuperWrapper = styled.div`
  height: 100%;
  .super-list-holder {
    display: flex;
    overflow: auto;
    position: relative;
    ${(props) => {
      if (props.pinnedColumns) {
        const leftPadding = props.pinnedColumns * 300;
        return `& > .smooth-dnd-container {
            padding-left: ${leftPadding}px;
          }`;
      }
    }}
    & > .smooth-dnd-container > .smooth-dnd-draggable-wrapper {
      padding-top: 20px;
    }
    // & > .smooth-dnd-container {
    //   width: 100%;
    //   height: 100%;
    // }
  }
  .super-list {
    width: 300px;
  }
`;

const PinnedColumnsContainer = styled.div`
  z-index: 10;
  display: flex;
  // position: fixed;
  padding-top: 20px;
  position: absolute;
  box-shadow: 5px 0 5px -5px rgba(0, 0, 0, 0.1);
  ${(props) => {
    // if (props.pinnedColumns) {
    //   const leftPadding = props.pinnedColumns * 300;
    //   return `margin-left: -${leftPadding}px;`;
    // }
  }} // & > .smooth-dnd-draggable-wrapper {
  //   padding-top: 20px;
  // }
`;

const VisibleColumnsContainer = styled.div`
  display: flex;
  padding-top: 20px;
  position: absolute;
  overflow-y: scroll;
  width: 100%;
  ${(props) => {
    if (props.pinnedColumns) {
      const leftPadding = props.pinnedColumns * 300;
      return `
        margin-left: ${leftPadding}px;
        width: calc(100% - ${leftPadding}px);
      `;
    }
  }}
  .column-drag-handle {
    ${(props) => (props.moveEnabled ? `cursor: move;` : `cursor: default;`)}
  }
`;

const HeaderLabel = styled.div`
  padding: 0 16px;
  width: calc(100% - 24px);
`;

const LoadMoreLabel = styled(HeaderLabel)`
  color: #1908f7;
  font-weight: 400;
  font-size: 16px;
  line-height: 21px;
`;

const ListHeader = styled.div`
  width: 280px;
  height: 55px;
  margin: 0 auto 20px;
  background: #eaeaf8;
  // box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.15);
  border-radius: 5px;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  word-break: break-word;
`;

const OtherListHeader = styled(ListHeader)`
  width: 280px;
  cursor: pointer;
  .count {
    background: #f4f5fa;
    width: fit-content;
    height: 20px;
    font-size: 12px;
    text-align: center;
    padding: 0px 4px;
    border-radius: 5px;
  }
`;

const ListItem = styled.div`
  height: 100px;
  background: #ffffff;
  border: 1px solid #e7e7e7;
  border-radius: 8px;
  margin-bottom: 20px;
  width: 100%;
  display: flex;
  justify-self: center;
`;

SuperBoard.defaultProps = {
  components: {
    ListHeader: (props) => {
      const { list } = props;
      return (
        <ListHeader>
          <HeaderLabel>{list.title}</HeaderLabel>
        </ListHeader>
      );
    },
    Card: (props) => {
      const { item } = props;
      return <ListItem>{item.label}</ListItem>;
    }
  },
  restrictCardDragCheck: false
};

export default SuperBoard;
